"use client";

import { useState, useCallback } from "react";
import { useLocalization } from "../../lib/translation";
import {
  XLogo,
  LinkedinLogo,
  FacebookLogo,
  Link,
  Check,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { SocialIconLink } from "../SocialIconLink";

export interface ArticleShareSectionProps {
  /** Article URL to share */
  url: string;
  /** Article title for share text */
  title: string;
  /** Layout direction. Defaults to `horizontal`. */
  layout?: "horizontal" | "vertical";
  /**
   * Show the translated "Share this article" heading, rendered at
   * `headingLevel`. Defaults to `true`.
   */
  showTitle?: boolean;
  /**
   * Heading level of the section title, to fit the page outline. Defaults to
   * `3`.
   * @default 3 
   */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  /** Custom className */
  className?: string;
}

/**
 * ArticleShareSection - Social sharing buttons for articles
 */
export function ArticleShareSection({
  url,
  title,
  layout = "horizontal",
  showTitle = true,
  headingLevel = 3,
  className,
}: ArticleShareSectionProps) {
  const Heading = `h${headingLevel}` as const;
  const { translate: t, resolvedLanguage } = useLocalization();
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const twitterLabel = t("shareTwitter", "Share on X");
  const linkedinLabel = t("shareLinkedIn", "Share on LinkedIn");
  const facebookLabel = t("shareFacebook", "Share on Facebook");
  const copyLinkLabel = copied
    ? t("articleLinkCopied", "Link copied!")
    : t("articleCopyLink", "Copy link");

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
  };

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  const isVertical = layout === "vertical";

  const buttonBaseClass = cn(
    "inline-flex items-center justify-center",
    "w-10 h-10 rounded-full",
    "border border-border",
    "text-muted-foreground",
    "transition-colors duration-200",
    "hover:bg-foreground hover:text-background hover:border-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  );

  return (
    <div
      lang={resolvedLanguage}
      className={cn(
        isVertical ? "flex flex-col items-start gap-4" : "space-y-4",
        className,
      )}
    >
      {/* Title */}
      {showTitle && (
        <Heading
          className={cn(
            "font-body text-sm font-medium",
            "text-muted-foreground uppercase tracking-wider",
          )}
        >
          {t("articleShareTitle", "Share this article")}
        </Heading>
      )}

      {/* Buttons */}
      <div className={cn("flex gap-3", isVertical && "flex-col")}>
        <SocialIconLink
          href={shareLinks.twitter}
          label={twitterLabel}
          variant="chip"
          size="sm"
        >
          <XLogo />
        </SocialIconLink>

        <SocialIconLink
          href={shareLinks.linkedin}
          label={linkedinLabel}
          variant="chip"
          size="sm"
        >
          <LinkedinLogo />
        </SocialIconLink>

        <SocialIconLink
          href={shareLinks.facebook}
          label={facebookLabel}
          variant="chip"
          size="sm"
        >
          <FacebookLogo />
        </SocialIconLink>

        {/* Copy link */}
        <button
          type="button"
          onClick={copyToClipboard}
          className={cn(
            buttonBaseClass,
            // Status fill tokens: --color-on-status ink on --color-success
            // passes 3:1 non-text contrast in every theme (white on
            // green-500 was about 2.3:1).
            copied &&
              "bg-[var(--color-success)] text-[var(--color-on-status)] border-[var(--color-success)]",
          )}
          aria-label={copyLinkLabel}
          title={copyLinkLabel}
        >
          {copied ? (
            <Check size={16} aria-hidden="true" />
          ) : (
            <Link size={16} aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Copy feedback. A polite live region that is always mounted, so the
          confirmation is announced when its text appears; while empty it is
          visually hidden and out of the layout. green-800 ink: green-600 is
          only ~3:1 on white, below WCAG AA for small text. */}
      <span
        role="status"
        aria-live="polite"
        className={
          copied
            ? "text-sm font-body text-green-800 dark:text-green-400"
            : "sr-only"
        }
      >
        {copied ? t("articleLinkCopied", "Link copied!") : null}
      </span>
    </div>
  );
}

ArticleShareSection.displayName = "ArticleShareSection";
